import { useEffect, useRef } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

const FADE_DURATION_MS = 800;

export const useDigitalCardMusicPlayback = (enabled: boolean) => {
  const audioRef = useRef<HTMLAudioElement>();
  const fadeRef = useRef<ReturnType<typeof setInterval>>();

  const stopMusic = useEventCallback(() => {
    clearInterval(fadeRef.current);
    fadeRef.current = undefined;
    audioRef.current?.pause();
    audioRef.current = undefined;
  });

  useEffect(() => stopMusic, [enabled, stopMusic]);

  const playMusic = (url: string | undefined) => {
    stopMusic();
    if (!enabled || !url) return;

    const audio = new Audio(url);
    audio.loop = false;
    audioRef.current = audio;
    // Called directly from the click handler to preserve user activation.
    void audio.play().catch(() => undefined);
  };

  const fadeOutMusic = () => {
    const audio = audioRef.current;
    if (!audio || fadeRef.current !== undefined) return;

    const startedAt = performance.now();
    const initialVolume = audio.volume;
    fadeRef.current = setInterval(() => {
      const progress = Math.min((performance.now() - startedAt) / FADE_DURATION_MS, 1);
      audio.volume = initialVolume * (1 - progress);
      if (progress === 1) stopMusic();
    }, 25);
  };

  return { playMusic, fadeOutMusic };
};
